import React, { useCallback, useMemo, useState } from 'react';
import { Button, Card, Grid, Space, Tabs, Typography } from 'antd';
import { ReloadOutlined, WarningOutlined } from '@ant-design/icons';
import type { Dayjs } from 'dayjs';
import { adminApi } from '@/services/api';
import type { AnalyticsDateFilters } from '@/services/api';
import { ExportButton } from '@/components/ExportButton';
import { exportOptions } from '@/constants/exportOptions';
import { AnalyticsDateRangeFilter } from '@/components';
import type { AnalyticsDateRange, AnalyticsPeriod } from '@/components';
import { usePagedList, type PagedListState } from '@/hooks/usePagedList';
import type {
  CardGenerateAuditItem,
  CardGenerateAuditResponse,
  EarnLimitFlagItem,
  EarnLimitFlagsResponse,
  RiskListName,
  RiskListPage,
  SuspectMatchItem,
  SuspectMatchesResponse,
  SuspiciousPlayer,
} from '@/types';
import { ListTabLabel } from './ListParts';
import { RiskPlayersTab } from './RiskPlayersTab';
import { RISK_LISTS, riskRowKey } from './riskLists';
import { SuspectMatchesTab, type SuspectSummary } from './SuspectMatchesTab';
import { EarnFlagsTab, type EarnFlagsSummary } from './EarnFlagsTab';
import { CardGeneratesTab, type CardGeneratesSummary } from './CardGeneratesTab';

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const byId = (r: { id: string }) => r.id;

/** One of the six player risk lists, a page at a time. */
function useRiskList(name: RiskListName, filters: AnalyticsDateFilters) {
  const fetchPage = useCallback(
    async (offset: number, limit: number) => {
      const { data } = await adminApi.getRiskList(name, { ...filters, offset, limit });
      const page = data as RiskListPage;
      return { rows: page.rows, total: page.total, available: page.listed };
    },
    [name, filters]
  );
  return usePagedList<SuspiciousPlayer>(fetchPage, riskRowKey);
}

/**
 * Risks & Hacks: the player risk lists, suspect matches, earns over the daily limit and card generates, moved
 * here from Analytics (27 Oct 2026). Every list loads on its own, so a slow one holds up only its own tab, and
 * shows the count of everything it flags with its rows a page at a time.
 */
export const RisksPage: React.FC = () => {
  const screens = useBreakpoint();
  const [period, setPeriod] = useState<AnalyticsPeriod>('last_7_days');
  const [customRange, setCustomRange] = useState<[Dayjs, Dayjs] | null>(null);

  const filters = useMemo<AnalyticsDateFilters>(
    () =>
      period === 'custom' && customRange
        ? {
            period: 'custom',
            startDate: customRange[0].format('YYYY-MM-DD'),
            endDate: customRange[1].format('YYYY-MM-DD'),
          }
        : { period },
    [period, customRange]
  );

  const risk: Record<RiskListName, PagedListState<SuspiciousPlayer, undefined>> = {
    suspiciousWinRates: useRiskList('suspiciousWinRates', filters),
    economyOutliers: useRiskList('economyOutliers', filters),
    rapidProgression: useRiskList('rapidProgression', filters),
    largeTransactions: useRiskList('largeTransactions', filters),
    unpurchasedPremium: useRiskList('unpurchasedPremium', filters),
    divisionMatchMismatch: useRiskList('divisionMatchMismatch', filters),
  };

  const suspect = usePagedList<SuspectMatchItem, SuspectSummary>(
    useCallback(
      async (offset: number, limit: number) => {
        const { data } = await adminApi.getSuspectMatches({ ...filters, offset, limit });
        const { matches, ...summary } = data as SuspectMatchesResponse;
        return { rows: matches, total: summary.total, available: summary.total, summary };
      },
      [filters]
    ),
    byId
  );

  const earn = usePagedList<EarnLimitFlagItem, EarnFlagsSummary>(
    useCallback(
      async (offset: number, limit: number) => {
        const { data } = await adminApi.getEarnLimitFlags({ ...filters, offset, limit });
        const { flags, ...summary } = data as EarnLimitFlagsResponse;
        return { rows: flags, total: summary.total, available: summary.total, summary };
      },
      [filters]
    ),
    byId
  );

  const cards = usePagedList<CardGenerateAuditItem, CardGeneratesSummary>(
    useCallback(
      async (offset: number, limit: number) => {
        const { data } = await adminApi.getCardGenerateAudit({ ...filters, offset, limit });
        const { rows, ...summary } = data as CardGenerateAuditResponse;
        return { rows, total: summary.total, available: summary.total, summary };
      },
      [filters]
    ),
    byId
  );

  const all = [...Object.values(risk), suspect, earn, cards];
  const loading = all.some((s) => s.loading);

  return (
    <div>
      <div className="page-header">
        <Space style={{ justifyContent: 'space-between', width: '100%' }} wrap>
          <div>
            <Title level={3} style={{ marginBottom: 0 }}>
              <WarningOutlined style={{ color: '#ff4d4f', marginRight: 8 }} />
              Risks &amp; Hacks
            </Title>
            <Text type="secondary">
              Players and matches the server flags as possible cheats or hacks. Each tab shows the
              count of everything it flags; load more rows at the bottom.
            </Text>
          </div>
          <Space wrap>
            <ExportButton
              options={[exportOptions.riskAnalytics, exportOptions.suspectMatches]}
              buttonText="Export"
            />
            <AnalyticsDateRangeFilter
              value={{ period, customRange: customRange ?? undefined }}
              loading={loading}
              onApply={(next: AnalyticsDateRange) => {
                setPeriod(next.period);
                setCustomRange(next.customRange ?? null);
              }}
            />
            <Button
              icon={<ReloadOutlined />}
              loading={loading}
              onClick={() => all.forEach((s) => s.reload())}
            >
              Reload
            </Button>
          </Space>
        </Space>
      </div>

      <Card styles={{ body: { padding: screens.xs ? 8 : 24 } }}>
        <Tabs
          items={[
            ...RISK_LISTS.map((info) => ({
              key: info.name,
              label: <ListTabLabel title={info.title} state={risk[info.name]} color={info.color} />,
              children: <RiskPlayersTab info={info} state={risk[info.name]} />,
            })),
            {
              key: 'suspect-matches',
              label: <ListTabLabel title="Suspect Matches" state={suspect} />,
              children: <SuspectMatchesTab state={suspect} />,
            },
            {
              key: 'earn-over-limit',
              label: <ListTabLabel title="Earn over limit" state={earn} />,
              children: <EarnFlagsTab state={earn} />,
            },
            {
              key: 'card-generates',
              label: <ListTabLabel title="Card generates" state={cards} color="default" />,
              children: <CardGeneratesTab state={cards} />,
            },
          ]}
        />
      </Card>
    </div>
  );
};

export default RisksPage;
